import '@blockly/field-dependent-dropdown';
import * as Blockly from 'blockly';
import { javascriptGenerator } from 'blockly/javascript';
import { ProjectSecretService } from 'src/app/shared/services/project-secret.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { BLOCKLY_COLORS } from './blocky-types.types';

/**
 * Builds the category → options mapping for the unified BLOCK_STACK block.
 */
function buildStackMapping(rss: RouteShareService, secretService: ProjectSecretService): {
    categoryOptions: [string, string][];
    optionMapping: Record<string, [string, string][]>;
} {
    const noSkillsLabel: [string, string] = ['(noch keine Skills angelegt)', ''];
    const categoryDefs: { key: string; label: string; options: () => [string, string][] }[] = [
        { key: 'WEBHOOK', label: '⚓️ Webhook', options: () => rss._allWebhooks().map(v => [v, v]) },
        { key: 'DATABASE', label: '🗄️ Datenbank', options: () => rss._allDataBases().map(v => [v, v]) },
        { key: 'WENNDANN', label: '🔀 Wenn-Dann', options: () => rss._allWennDann().map(v => [v, v]) },
        { key: 'ONLINESOURCE', label: '🌐 Onlinequellen', options: () => rss._allOnlineSources().map(v => [v, v]) },
        { key: 'OFFLINEWEBSITE', label: '📄 Offline-Websites', options: () => rss._allOfflineWebsites().map(v => [v, v]) },
        { key: 'FUSIONVIEW', label: '🔗 Fusion-View', options: () => rss._allFusionViews().map(v => [v, v]) },
        { key: 'PROJECTSECRET', label: '🔑 Projekt-Secret', options: () => secretService.projectSecrets.map(s => [s.name, s.name] as [string, string]) }
    ];

    const categoryOptions: [string, string][] = [];
    const optionMapping: Record<string, [string, string][]> = {};

    // Show only categories that actually have skill IDs.
    for (const cat of categoryDefs) {
        const items = cat.options();
        if (items.length === 0) continue;
        categoryOptions.push([cat.label, cat.key]);
        optionMapping[cat.key] = items;
    }

    // If nothing exists at all, show a single explicit empty-state entry.
    if (categoryOptions.length === 0) {
        categoryOptions.push(noSkillsLabel);
        optionMapping[''] = [noSkillsLabel];
    }

    return { categoryOptions, optionMapping };
}

let _registered = false;

/**
 * Resets the registration flag so that registerStackBlock() re-registers the
 * BLOCK_STACK block with fresh data on the next call. Call this before
 * deleting BLOCK_STACK from Blockly.Blocks when switching bots.
 */
export function resetStackBlockRegistration(): void {
    _registered = false;
}

/**
 * Registers the unified BLOCK_STACK block with two dependent dropdowns.
 * First dropdown: Category (Webhook, Datenbank, Wenn-Dann, etc.)
 * Second dropdown: Items of the selected category.
 * Idempotent – skips if already registered.
 */
export function registerStackBlock(rss: RouteShareService, secretService: ProjectSecretService): void {
    if (_registered) return;
    _registered = true;

    const { categoryOptions, optionMapping } = buildStackMapping(rss, secretService);

    Blockly.defineBlocksWithJsonArray([
        {
            type: 'BLOCK_STACK',
            message0: 'Skill-Typ: %1 Skill-ID: %2',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'STACK_CATEGORY',
                    options: categoryOptions,
                },
                {
                    type: 'field_dependent_dropdown',
                    name: 'STACK_VALUE',
                    parentName: 'STACK_CATEGORY',
                    optionMapping: optionMapping,
                    defaultOptions: [['(noch keine Skills angelegt)', '']],
                }
            ],
            output: 'String',
            colour: BLOCKLY_COLORS.VARIABLES,
            tooltip: 'Referenziert einen bestehenden Eintrag aus einer der Projekt-Ressourcen anhand seines Namens. Wähle zuerst über „Skill-Typ" eine Kategorie (⚓️ Webhook, 🗄️ Datenbank, 🔀 Wenn-Dann, 🌐 Onlinequellen, 📄 Offline-Websites, 🔗 Fusion-View oder 🔑 Projekt-Secret), anschließend über „Skill-ID" den konkreten Eintrag dieser Kategorie.\n\n🆔 BLOCK_STACK',
        }
    ]);

    javascriptGenerator.forBlock['BLOCK_STACK'] = (block) => {
        const value = block.getFieldValue('STACK_VALUE');
        return [`'${value}'`, 0];
    };
}
